import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Bell } from "lucide-react";
import {
  dismissMaintenanceNotice,
  fetchNotifications,
  markNotificationRead,
  type MaintenanceNotice,
  type NotificationFeed,
} from "@/lib/api";
import {
  KNOWLEDGE_ASSISTANT_PRODUCT_CONTEXT,
  openNoticeHref,
  publishProductNotifications,
} from "@/lib/productNotifications";

const POLL_MS = 60_000;

interface NotificationsProps {
  teamsToken?: string | null;
  reserveMenuSpace?: boolean;
}

export function Notifications({ teamsToken, reserveMenuSpace = false }: NotificationsProps) {
  const [feed, setFeed] = useState<NotificationFeed | null>(null);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const load = useCallback(async () => {
    try {
      const next = await fetchNotifications(teamsToken);
      setFeed(next);
      if (next.enabled) {
        publishProductNotifications({
          productContext: next.productContext || KNOWLEDGE_ASSISTANT_PRODUCT_CONTEXT,
          unreadCount: next.unreadCount,
          items: next.items,
          viewAllHref: next.viewAllHref,
          viewAllLabel: next.viewAllLabel,
        });
      }
    } catch {
      setFeed((current) => current);
    }
  }, [teamsToken]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => {
      window.clearInterval(timer);
      publishProductNotifications({
        productContext: KNOWLEDGE_ASSISTANT_PRODUCT_CONTEXT,
        unreadCount: 0,
        items: [],
      });
    };
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!feed?.enabled) return null;

  const unread = feed.unreadCount;
  const label = `Knowledge Assistant notices, ${unread} unread`;

  const onOpenItem = async (id: string, href?: string | null) => {
    setOpen(false);
    try {
      const next = await markNotificationRead(id, teamsToken);
      setFeed(next);
      publishProductNotifications({
        productContext: next.productContext || KNOWLEDGE_ASSISTANT_PRODUCT_CONTEXT,
        unreadCount: next.unreadCount,
        items: next.items,
        viewAllHref: next.viewAllHref,
        viewAllLabel: next.viewAllLabel,
      });
    } catch {
      /* Keep the list. The next refresh will retry. */
    }
    if (href) openNoticeHref(href);
  };

  const onDismissMaintenance = async (id: string) => {
    try {
      const next = await dismissMaintenanceNotice(id, teamsToken);
      setFeed(next);
    } catch {
      /* The banner stays until the next refresh. */
    }
  };

  return (
    <div
      className={`flex items-start justify-end gap-2 px-3 pt-3 ${reserveMenuSpace ? "pl-12" : ""}`}
    >
      {feed.maintenance ? (
        <MaintenanceBanner notice={feed.maintenance} onDismiss={onDismissMaintenance} />
      ) : (
        <div className="min-w-0 flex-1" />
      )}
      <div className="relative shrink-0" ref={panelRef}>
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-surface/80 text-text-muted shadow-sm backdrop-blur-sm transition-colors hover:bg-surface hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ndc-red"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={label}
          title={label}
          onClick={() => setOpen((value) => !value)}
        >
          <Bell className="h-4 w-4" aria-hidden />
          {unread > 0 ? (
            <span className="absolute -right-1 -top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-ndc-red px-1 text-[10px] font-semibold leading-4 text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          ) : null}
        </button>
        {open ? (
          <div
            role="menu"
            aria-labelledby={titleId}
            className="absolute right-0 z-40 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-surface shadow-[0_8px_30px_rgba(15,26,46,0.12)]"
          >
            <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
              <span id={titleId} className="text-[13px] font-medium text-text">
                Knowledge Assistant notices
              </span>
              <span className="text-[12px] text-text-muted">
                {unread > 0 ? `${unread} unread` : "All caught up"}
              </span>
            </div>
            {feed.unavailable ? (
              <p className="px-3 py-4 text-[13px] text-text-muted">
                Notices are temporarily unavailable.
              </p>
            ) : feed.items.length === 0 ? (
              <p className="px-3 py-4 text-[13px] text-text-muted">Nothing new right now.</p>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                {feed.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    className="flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ndc-red"
                    onClick={() => void onOpenItem(item.id, item.href)}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] text-text">{item.text}</span>
                      <span className="mt-0.5 block text-[12px] text-text-muted">
                        {item.actor ? `${item.actor} · ` : ""}
                        {formatWhen(item.createdAt)}
                      </span>
                    </span>
                    {item.read ? null : (
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-ndc-red" aria-label="Unread" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MaintenanceBanner({
  notice,
  onDismiss,
}: {
  notice: MaintenanceNotice;
  onDismiss: (id: string) => void;
}) {
  const showGotIt = notice.type === "scheduled" || notice.type === "completion";
  return (
    <div
      role="status"
      className="min-w-0 flex-1 rounded-xl border border-border bg-surface px-3 py-2 text-[13px] text-text shadow-sm"
    >
      {notice.title ? <p className="font-medium">{notice.title}</p> : null}
      {notice.body ? <p className="mt-1 whitespace-pre-wrap text-text-muted">{notice.body}</p> : null}
      {notice.startsAt || notice.endsAt ? (
        <p className="mt-1 text-[12px] text-text-muted">
          {notice.startsAt ? `Starts ${formatWhen(notice.startsAt)}` : ""}
          {notice.startsAt && notice.endsAt ? " · " : ""}
          {notice.endsAt ? `Ends ${formatWhen(notice.endsAt)}` : ""}
        </p>
      ) : null}
      {showGotIt || notice.learnMoreUrl ? (
        <div className="mt-2 flex items-center gap-2">
          {notice.learnMoreUrl ? (
            <a
              href={notice.learnMoreUrl}
              className="font-medium text-ndc-red underline-offset-2 hover:underline"
            >
              Learn more
            </a>
          ) : null}
          {showGotIt ? (
            <button
              type="button"
              className="rounded-md bg-ndc-red px-2.5 py-1 text-[12px] font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ndc-red focus-visible:ring-offset-2"
              onClick={() => onDismiss(notice.id)}
            >
              Ok, got it
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function formatWhen(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}
